import React, { useCallback, useImperativeHandle, useState } from "react";
import { Tabs } from "@base-ui/react";
import classNames from "classnames";
import { StepsProps, StepsRef } from "./types";
import styles from "./Steps.module.scss";

export const Steps = <T extends string>({
    steps,
    defaultStep,
    onStepChange,
    className,
    classNames: classNamesProp,
    stepsRef: ref,
}: StepsProps<T> & { stepsRef: React.RefObject<StepsRef | null> }) => {
    const [currentStepId, setCurrentStepId] = useState<T>(
        defaultStep ?? steps[0]?.id ?? "",
    );

    const currentStepIndex = steps.findIndex(
        (step) => step.id === currentStepId,
    );

    const handleStepChange = useCallback(
        (stepId: T) => {
            setCurrentStepId(stepId);
            onStepChange?.(stepId);
        },
        [onStepChange],
    );

    const goToStep = useCallback(
        (stepId: T) => {
            const targetStepIndex = steps.findIndex(
                (step) => step.id === stepId,
            );
            if (targetStepIndex !== -1) {
                handleStepChange(stepId);
            }
        },
        [steps, handleStepChange],
    );

    const nextStep = useCallback(() => {
        if (currentStepIndex < steps.length - 1) {
            const nextStepId = steps[currentStepIndex + 1].id;
            handleStepChange(nextStepId);
        }
    }, [currentStepIndex, steps, handleStepChange]);

    const prevStep = useCallback(() => {
        if (currentStepIndex > 0) {
            const prevStepId = steps[currentStepIndex - 1].id;
            handleStepChange(prevStepId);
        }
    }, [currentStepIndex, steps, handleStepChange]);

    useImperativeHandle(
        ref,
        () => ({
            currentStep: currentStepId,
            next: nextStep,
            prev: prevStep,
            go: goToStep,
        }),
        [currentStepId, nextStep, prevStep, goToStep],
    );

    const isStepDisabled = useCallback(
        (stepIndex: number) => stepIndex > currentStepIndex,
        [currentStepIndex],
    );

    const handleTabChange = useCallback(
        (value: T | null) => {
            if (typeof value === "string") {
                const targetStepIndex = steps.findIndex(
                    (step) => step.id === value,
                );
                if (
                    targetStepIndex !== -1 &&
                    !isStepDisabled(targetStepIndex)
                ) {
                    handleStepChange(value);
                }
            }
        },
        [steps, isStepDisabled, handleStepChange],
    );

    if (steps.length === 0) {
        return null;
    }

    return (
        <Tabs.Root
            value={currentStepId}
            onValueChange={handleTabChange}
            className={classNames(
                styles.stepsRoot,
                classNamesProp?.root,
                className,
            )}
        >
            <Tabs.List
                className={classNames(styles.stepsList, classNamesProp?.list)}
            >
                {steps.map((step, index) => (
                    <Tabs.Tab
                        key={step.id}
                        value={step.id}
                        disabled={isStepDisabled(index)}
                        className={classNames(
                            styles.stepsTab,
                            classNamesProp?.tab,
                            {
                                [styles.stepsTabActive]:
                                    step.id === currentStepId,
                                [styles.stepsTabDisabled]:
                                    isStepDisabled(index),
                                [styles.stepsTabCompleted]:
                                    index < currentStepIndex + 1,
                            },
                        )}
                    >
                        {step.header}
                    </Tabs.Tab>
                ))}
                <Tabs.Indicator
                    className={classNames(
                        styles.stepsIndicator,
                        classNamesProp?.indicator,
                    )}
                />
            </Tabs.List>

            {steps.map((step) => (
                <Tabs.Panel
                    key={step.id}
                    value={step.id}
                    className={classNames(
                        styles.stepsPanel,
                        classNamesProp?.panel,
                    )}
                >
                    {step.content}
                </Tabs.Panel>
            ))}
        </Tabs.Root>
    );
};

Steps.displayName = "Steps";
